// Aesthetic type data for SEO pages
// Following the pattern from kibbeData.ts

export interface AestheticType {
  id: string;
  name: string;
  tagline: string;
  description: string;
  characteristics: string[];
  colorPalette: { name: string; hex: string }[];
  keyPieces: string[];
  celebrities: string[];
  doStyles: string[];
  dontStyles: string[];
  relatedAesthetics: string[];
  seasonConnection?: string; // Connection to color analysis seasons
  outfitIdeas: { name: string; description: string; pieces: string[] }[];
}

export const aestheticTypes: Record<string, AestheticType> = {
  "old-money": {
    id: "old-money",
    name: "Old Money",
    tagline: "Timeless elegance that whispers wealth, never shouts it.",
    description:
      "The Old Money aesthetic embodies understated luxury and generational wealth. It's characterized by classic, timeless pieces that prioritize quality over trends. Think Ivy League campuses, country clubs, and European aristocracy. This style values heritage brands, natural fabrics, and a polished-but-effortless appearance that suggests you've never had to try too hard.",
    characteristics: [
      "Understated luxury over logos",
      "Neutral, muted color palette",
      "Quality natural fabrics (cashmere, silk, wool)",
      "Classic, timeless silhouettes",
      "Minimal, elegant jewelry",
      "Polished but effortless appearance",
      "Heritage and legacy-focused",
    ],
    colorPalette: [
      { name: "Navy", hex: "#1B365D" },
      { name: "Cream", hex: "#FFFDD0" },
      { name: "Camel", hex: "#C19A6B" },
      { name: "Burgundy", hex: "#722F37" },
      { name: "Forest Green", hex: "#228B22" },
      { name: "Charcoal", hex: "#36454F" },
    ],
    keyPieces: [
      "Cashmere sweater",
      "Tennis bracelet",
      "Pearl earrings",
      "Tailored blazer",
      "Loafers",
      "Silk scarf",
      "White button-down shirt",
      "Camel coat",
    ],
    celebrities: ["Carolyn Bessette-Kennedy", "Grace Kelly", "Sofia Richie", "Gwyneth Paltrow", "Amal Clooney"],
    doStyles: [
      "Invest in quality basics that last",
      "Choose neutral colors as your foundation",
      "Opt for natural fabrics like cashmere and silk",
      "Keep jewelry minimal and classic",
      "Focus on fit and tailoring",
      "Layer thoughtfully with texture",
    ],
    dontStyles: [
      "Avoid visible logos and brand names",
      "Skip trendy fast fashion pieces",
      "Don't over-accessorize",
      "Avoid overly bright or neon colors",
      "Skip distressed or ripped items",
      "Don't mix too many patterns",
    ],
    relatedAesthetics: ["quiet-luxury", "clean-girl"],
    seasonConnection: "Often suits Cool or Soft seasons with muted, sophisticated tones",
    outfitIdeas: [
      {
        name: "Weekend Brunch",
        description: "Effortlessly polished for a leisurely morning",
        pieces: ["Cream cashmere sweater", "Tailored navy trousers", "Leather loafers", "Pearl studs"],
      },
      {
        name: "Country Club Ready",
        description: "Classic sporty elegance",
        pieces: ["White polo shirt", "Pleated tennis skirt", "Cable-knit sweater tied over shoulders", "White sneakers"],
      },
      {
        name: "City Sophisticate",
        description: "Polished urban elegance",
        pieces: ["Camel wool coat", "Silk blouse", "High-waisted trousers", "Structured leather bag"],
      },
    ],
  },
  "clean-girl": {
    id: "clean-girl",
    name: "Clean Girl",
    tagline: "Effortless beauty that looks like you woke up flawless.",
    description:
      "The Clean Girl aesthetic celebrates natural beauty and minimalist style. It's all about looking polished without appearing to try too hard. Think dewy skin, slicked-back hair, gold hoops, and neutral basics. This aesthetic prioritizes skincare over makeup and quality basics over trendy pieces.",
    characteristics: [
      "Dewy, healthy-looking skin",
      "Minimal, natural makeup",
      "Slicked-back hairstyles",
      "Gold jewelry, especially hoops",
      "Neutral, earthy color palette",
      "Quality basics over trends",
      "Wellness-focused lifestyle",
    ],
    colorPalette: [
      { name: "White", hex: "#FFFFFF" },
      { name: "Beige", hex: "#F5F5DC" },
      { name: "Tan", hex: "#D2B48C" },
      { name: "Black", hex: "#000000" },
      { name: "Olive", hex: "#808000" },
      { name: "Cream", hex: "#FFFDD0" },
    ],
    keyPieces: [
      "Gold hoop earrings",
      "White tank top",
      "High-waisted jeans",
      "Neutral sneakers",
      "Mini shoulder bag",
      "Oversized blazer",
      "Slip dress",
      "Matching loungewear set",
    ],
    celebrities: ["Hailey Bieber", "Kendall Jenner", "Rosie Huntington-Whiteley", "Zendaya", "Sofia Richie"],
    doStyles: [
      "Prioritize skincare over heavy makeup",
      "Invest in quality neutral basics",
      "Choose minimal gold jewelry",
      "Keep hair sleek and simple",
      "Focus on fit and proportion",
      "Layer with intention",
    ],
    dontStyles: [
      "Avoid heavy, dramatic makeup",
      "Skip overly trendy statement pieces",
      "Don't overload on accessories",
      "Avoid clashing patterns",
      "Skip dated or worn-looking items",
      "Don't forget grooming details",
    ],
    relatedAesthetics: ["old-money", "quiet-luxury"],
    seasonConnection: "Works well with Warm seasons, especially True Spring or Light Spring",
    outfitIdeas: [
      {
        name: "Coffee Run",
        description: "Polished errand style",
        pieces: ["White ribbed tank", "Wide-leg linen pants", "Leather slides", "Gold hoops"],
      },
      {
        name: "Office Ready",
        description: "Professional minimalism",
        pieces: ["Oversized beige blazer", "White tee", "Tailored trousers", "Pointed-toe flats"],
      },
      {
        name: "Date Night",
        description: "Understated elegance",
        pieces: ["Satin slip dress", "Delicate gold necklace", "Strappy heels", "Mini clutch"],
      },
    ],
  },
  "dark-feminine": {
    id: "dark-feminine",
    name: "Dark Feminine",
    tagline: "Mysterious allure meets unapologetic power.",
    description:
      "The Dark Feminine aesthetic embraces the mysterious, powerful, and seductive side of femininity. It draws from archetypes like the femme fatale, the enchantress, and the queen. This style is about commanding presence through dark colors, luxurious textures, and bold silhouettes that exude confidence and mystique.",
    characteristics: [
      "Dark, moody color palette",
      "Luxurious textures (velvet, silk, leather)",
      "Bold, dramatic silhouettes",
      "Statement jewelry with dark stones",
      "Smoldering, dramatic makeup",
      "Confident, powerful energy",
      "Gothic and romantic influences",
    ],
    colorPalette: [
      { name: "Black", hex: "#000000" },
      { name: "Deep Burgundy", hex: "#4A0E0E" },
      { name: "Emerald", hex: "#046307" },
      { name: "Deep Purple", hex: "#301934" },
      { name: "Midnight Blue", hex: "#191970" },
      { name: "Oxblood", hex: "#4A0000" },
    ],
    keyPieces: [
      "Black lace bodysuit",
      "Velvet blazer",
      "Leather pants",
      "Silk slip dress",
      "Statement earrings",
      "Dark red lipstick",
      "Stiletto heels",
      "Structured corset top",
    ],
    celebrities: ["Angelina Jolie", "Megan Fox", "Dita Von Teese", "Monica Bellucci", "Rihanna"],
    doStyles: [
      "Embrace dark, rich colors",
      "Mix textures like leather and lace",
      "Choose bold, statement pieces",
      "Invest in quality dark-toned basics",
      "Opt for dramatic makeup looks",
      "Accessorize with intention",
    ],
    dontStyles: [
      "Avoid overly bright or pastel colors",
      "Skip cutesy or girly elements",
      "Don't be afraid of bold choices",
      "Avoid looking unpolished",
      "Skip cheap-looking materials",
      "Don't underestimate the power of fit",
    ],
    relatedAesthetics: ["coquette", "mob-wife"],
    seasonConnection: "Perfect for Deep or Dark seasons like Deep Autumn or Dark Winter",
    outfitIdeas: [
      {
        name: "Evening Power",
        description: "Commanding presence for night out",
        pieces: ["Black velvet blazer", "Silk camisole", "Leather pants", "Pointed-toe boots"],
      },
      {
        name: "Romantic Edge",
        description: "Seductive with gothic undertones",
        pieces: ["Lace midi dress", "Emerald drop earrings", "Strappy heels", "Dark berry lip"],
      },
      {
        name: "Boss Energy",
        description: "Corporate with an edge",
        pieces: ["Structured black dress", "Gold statement cuff", "Stilettos", "Deep red nails"],
      },
    ],
  },
  coquette: {
    id: "coquette",
    name: "Coquette",
    tagline: "Flirtatious femininity with a touch of innocence.",
    description:
      "The Coquette aesthetic is all about playful, romantic femininity with a hint of seduction. Drawing from French girl style and ballet-inspired fashion, it features soft colors, delicate fabrics, bows, lace, and feminine silhouettes. It's flirtatious without being overtly sexy, innocent yet knowing.",
    characteristics: [
      "Soft pink and white color palette",
      "Bows, ribbons, and lace details",
      "Delicate, feminine silhouettes",
      "Ballet and French-inspired elements",
      "Soft, romantic makeup",
      "Dainty jewelry",
      "Playful yet sophisticated",
    ],
    colorPalette: [
      { name: "Blush Pink", hex: "#FFB6C1" },
      { name: "Cream White", hex: "#FFFDD0" },
      { name: "Baby Blue", hex: "#89CFF0" },
      { name: "Soft Lavender", hex: "#E6E6FA" },
      { name: "Rose", hex: "#FF007F" },
      { name: "Pearl", hex: "#FDEEF4" },
    ],
    keyPieces: [
      "Bow hair clips",
      "Lace-trimmed camisole",
      "Satin ballet flats",
      "Pearl jewelry",
      "Ruffle mini skirt",
      "Cardigan with ribbon ties",
      "Mary Jane shoes",
      "Delicate choker necklace",
    ],
    celebrities: ["Ariana Grande", "Lana Del Rey", "Sydney Sweeney", "Elle Fanning", "Lily-Rose Depp"],
    doStyles: [
      "Incorporate bows and ribbons",
      "Choose soft, feminine colors",
      "Layer delicate pieces",
      "Mix lace and satin textures",
      "Keep makeup soft and romantic",
      "Accessorize with pearls",
    ],
    dontStyles: [
      "Avoid harsh, dark colors",
      "Skip overly structured pieces",
      "Don't go too mature or severe",
      "Avoid chunky, heavy accessories",
      "Skip athletic or sporty elements",
      "Don't over-do the girliness",
    ],
    relatedAesthetics: ["balletcore", "cottagecore"],
    seasonConnection: "Ideal for Light or Soft seasons like Light Summer or Soft Summer",
    outfitIdeas: [
      {
        name: "Tea Party",
        description: "Perfectly pretty for afternoon events",
        pieces: ["Floral midi dress", "Satin bow headband", "Ballet flats", "Pearl bracelet"],
      },
      {
        name: "Day Dreamer",
        description: "Soft romantic for everyday",
        pieces: ["Lace-trimmed camisole", "High-waisted shorts", "Ribbon choker", "Woven basket bag"],
      },
      {
        name: "Date Night",
        description: "Flirtatious evening look",
        pieces: ["Satin slip dress", "Bow earrings", "Strappy kitten heels", "Soft pink lip"],
      },
    ],
  },
  "quiet-luxury": {
    id: "quiet-luxury",
    name: "Quiet Luxury",
    tagline: "True luxury needs no announcement.",
    description:
      "Quiet Luxury, also known as stealth wealth, is the antithesis of logo-heavy fashion. It focuses on exceptional quality, impeccable fit, and timeless design over flashy branding. This aesthetic values the connoisseur's appreciation of fine materials and craftsmanship that only those in the know will recognize.",
    characteristics: [
      "No visible logos or branding",
      "Exceptional fabric quality",
      "Impeccable tailoring and fit",
      "Neutral, understated color palette",
      "Investment pieces over trends",
      "Subtle sophistication",
      "Quality over quantity",
    ],
    colorPalette: [
      { name: "Ecru", hex: "#C2B280" },
      { name: "Slate Grey", hex: "#708090" },
      { name: "Oatmeal", hex: "#E8DCCA" },
      { name: "Espresso", hex: "#3C2415" },
      { name: "Ivory", hex: "#FFFFF0" },
      { name: "Charcoal", hex: "#36454F" },
    ],
    keyPieces: [
      "Cashmere turtleneck",
      "Tailored wool trousers",
      "Leather loafers",
      "Minimalist watch",
      "Quality leather bag",
      "Silk shirt",
      "Wool coat",
      "Fine knit sweater",
    ],
    celebrities: ["Gwyneth Paltrow", "Meghan Markle", "Cate Blanchett", "Jennifer Aniston", "Carolyn Bessette-Kennedy"],
    doStyles: [
      "Invest in timeless, quality pieces",
      "Focus on fit and tailoring",
      "Choose natural, luxurious fabrics",
      "Keep accessories minimal and refined",
      "Build a capsule wardrobe",
      "Prioritize versatility",
    ],
    dontStyles: [
      "Avoid visible logos and branding",
      "Skip trendy, fast-fashion pieces",
      "Don't sacrifice quality for quantity",
      "Avoid overly casual or sloppy looks",
      "Skip synthetic fabrics when possible",
      "Don't over-accessorize",
    ],
    relatedAesthetics: ["old-money", "clean-girl"],
    seasonConnection: "Complements Soft and Cool seasons with muted sophistication",
    outfitIdeas: [
      {
        name: "Business Meeting",
        description: "Effortlessly professional",
        pieces: ["Cashmere turtleneck", "Tailored wool trousers", "Leather belt", "Minimalist gold watch"],
      },
      {
        name: "Weekend Elegance",
        description: "Relaxed but refined",
        pieces: ["Fine knit sweater", "Silk-blend pants", "Leather loafers", "Structured tote"],
      },
      {
        name: "Evening Sophistication",
        description: "Understated glamour",
        pieces: ["Silk blouse", "High-waisted trousers", "Delicate gold jewelry", "Classic pumps"],
      },
    ],
  },
  "indie-sleaze": {
    id: "indie-sleaze",
    name: "Indie Sleaze",
    tagline: "Messy hair, don't care — but make it cool.",
    description:
      "Indie Sleaze brings back the chaotic, party-girl energy of the late 2000s and early 2010s. Think Brooklyn warehouse parties, band tees, smudged eyeliner, and an I-just-rolled-out-of-bed-looking-this-cool vibe. It's deliberately disheveled, unapologetically fun, and anti-polished.",
    characteristics: [
      "Deliberately disheveled styling",
      "Band tees and vintage finds",
      "Smudged, lived-in makeup",
      "Skinny jeans and leather jackets",
      "Messy, textured hair",
      "Mix of high and low fashion",
      "Nightlife-inspired edge",
    ],
    colorPalette: [
      { name: "Black", hex: "#000000" },
      { name: "Washed Grey", hex: "#808080" },
      { name: "Burgundy", hex: "#722F37" },
      { name: "Leopard Print", hex: "#C19A6B" },
      { name: "Denim Blue", hex: "#1560BD" },
      { name: "Metallics", hex: "#C0C0C0" },
    ],
    keyPieces: [
      "Vintage band tee",
      "Skinny jeans",
      "Leather jacket",
      "Combat boots",
      "Sequin mini skirt",
      "Oversized sunglasses",
      "Studded belt",
      "Ripped tights",
    ],
    celebrities: ["Alexa Chung", "Kate Moss", "Sky Ferreira", "The Olsen Twins (early era)", "Chloe Sevigny"],
    doStyles: [
      "Mix vintage with current pieces",
      "Embrace the disheveled look",
      "Layer unexpected combinations",
      "Include band or graphic tees",
      "Keep makeup smudgy and lived-in",
      "Thrift and vintage shop",
    ],
    dontStyles: [
      "Don't look too polished or perfect",
      "Avoid overly preppy pieces",
      "Skip the matching sets",
      "Don't over-coordinate",
      "Avoid ultra-feminine styling",
      "Don't try too hard",
    ],
    relatedAesthetics: ["streetwear", "dark-feminine"],
    seasonConnection: "Works with any season but often features muted, cool tones",
    outfitIdeas: [
      {
        name: "Concert Ready",
        description: "Night out at a show",
        pieces: ["Vintage band tee", "Skinny jeans", "Leather jacket", "Combat boots"],
      },
      {
        name: "Day After Party",
        description: "Effortlessly cool daytime look",
        pieces: ["Oversized blazer", "Mini dress", "Ripped tights", "Platform boots"],
      },
      {
        name: "Rooftop Hangout",
        description: "Casual but edgy",
        pieces: ["Cropped graphic tee", "High-waisted shorts", "Studded belt", "Sunglasses"],
      },
    ],
  },
  "coastal-grandmother": {
    id: "coastal-grandmother",
    name: "Coastal Grandmother",
    tagline: "Nancy Meyers movies as a lifestyle.",
    description:
      "The Coastal Grandmother aesthetic evokes the effortless elegance of a woman who summers in the Hamptons or along the Maine coast. Think linen everything, cozy knits, comfortable elegance, and a life filled with farmers markets, beach walks, and reading on the porch. It's about relaxed sophistication.",
    characteristics: [
      "Natural, breathable fabrics (linen, cotton)",
      "Soft, neutral color palette",
      "Comfortable yet elegant pieces",
      "Beach and coastal influences",
      "Quality over quantity",
      "Relaxed, lived-in styling",
      "Nancy Meyers movie vibes",
    ],
    colorPalette: [
      { name: "Soft White", hex: "#FAF9F6" },
      { name: "Sand", hex: "#C2B280" },
      { name: "Coastal Blue", hex: "#6F8FAF" },
      { name: "Sage", hex: "#9DC183" },
      { name: "Driftwood", hex: "#AF9B60" },
      { name: "Oatmeal", hex: "#E8DCCA" },
    ],
    keyPieces: [
      "Linen button-down shirt",
      "Wide-leg linen pants",
      "Straw tote bag",
      "White sneakers",
      "Cable-knit sweater",
      "Comfortable loafers",
      "Bucket hat",
      "Simple gold jewelry",
    ],
    celebrities: ["Diane Keaton", "Nancy Meyers characters", "Martha Stewart", "Ina Garten", "Meryl Streep"],
    doStyles: [
      "Choose natural, breathable fabrics",
      "Stick to a soft, neutral palette",
      "Prioritize comfort and ease",
      "Layer with cozy knits",
      "Keep accessories simple",
      "Embrace the relaxed fit",
    ],
    dontStyles: [
      "Avoid anything too tight or restrictive",
      "Skip synthetic fabrics",
      "Don't over-accessorize",
      "Avoid bold, bright colors",
      "Skip trendy, fast-fashion items",
      "Don't sacrifice comfort for style",
    ],
    relatedAesthetics: ["clean-girl", "cottagecore", "old-money"],
    seasonConnection: "Beautiful for Soft Summer or Light Summer with its muted, coastal tones",
    outfitIdeas: [
      {
        name: "Farmers Market",
        description: "Morning errands in style",
        pieces: ["Linen button-down", "Wide-leg pants", "Straw tote", "White sneakers"],
      },
      {
        name: "Beach Walk",
        description: "Comfortable coastal elegance",
        pieces: ["Oversized sweater", "Linen shorts", "Leather sandals", "Woven sun hat"],
      },
      {
        name: "Garden Party",
        description: "Effortless entertaining",
        pieces: ["Flowy linen dress", "Cardigan", "Ballet flats", "Simple gold earrings"],
      },
    ],
  },
  balletcore: {
    id: "balletcore",
    name: "Balletcore",
    tagline: "Grace and discipline wrapped in tulle.",
    description:
      "Balletcore brings the elegance of the dance studio into everyday fashion. It's characterized by wrap tops, leg warmers, ballet flats, soft pink tones, and delicate fabrics. This aesthetic celebrates the discipline and grace of ballet while making it wearable for non-dancers.",
    characteristics: [
      "Soft pink and white palette",
      "Ballet-inspired silhouettes",
      "Wrap tops and cardigans",
      "Leg warmers as accessories",
      "Ribbon and bow details",
      "Delicate, feminine fabrics",
      "Grace and poise",
    ],
    colorPalette: [
      { name: "Ballet Pink", hex: "#E8CEBF" },
      { name: "Pure White", hex: "#FFFFFF" },
      { name: "Blush", hex: "#DE5D83" },
      { name: "Soft Grey", hex: "#D3D3D3" },
      { name: "Black", hex: "#000000" },
      { name: "Mauve", hex: "#E0B0FF" },
    ],
    keyPieces: [
      "Wrap cardigan",
      "Ballet flats",
      "Tulle skirt",
      "Leg warmers",
      "Bodysuit",
      "Ribbon hair tie",
      "Off-shoulder top",
      "Satin pointe shoe-inspired flats",
    ],
    celebrities: ["Natalie Portman", "Misty Copeland", "Lily Collins", "Dakota Johnson", "Sydney Sweeney"],
    doStyles: [
      "Incorporate wrap and tie details",
      "Choose soft, feminine colors",
      "Opt for delicate fabrics",
      "Include ballet-inspired footwear",
      "Keep silhouettes graceful",
      "Add ribbon accents",
    ],
    dontStyles: [
      "Avoid bulky, oversized pieces",
      "Skip harsh colors",
      "Don't go too sporty or athletic",
      "Avoid heavy, clunky shoes",
      "Skip overly casual styling",
      "Don't forget posture and poise",
    ],
    relatedAesthetics: ["coquette", "cottagecore"],
    seasonConnection: "Perfect for Light or Soft seasons with delicate, muted tones",
    outfitIdeas: [
      {
        name: "Studio to Street",
        description: "Dance-inspired everyday",
        pieces: ["Wrap cardigan", "High-waisted leggings", "Ballet flats", "Hair ribbon"],
      },
      {
        name: "Romantic Evening",
        description: "Graceful date night",
        pieces: ["Off-shoulder top", "Tulle midi skirt", "Strappy heels", "Pearl earrings"],
      },
      {
        name: "Weekend Poise",
        description: "Casual with grace",
        pieces: ["Bodysuit", "Wide-leg pants", "Satin flats", "Simple bun"],
      },
    ],
  },
  "mob-wife": {
    id: "mob-wife",
    name: "Mob Wife",
    tagline: "Fendi fur, red nails, and an attitude that says 'fuggedaboutit.'",
    description:
      "The Mob Wife aesthetic channels the bold, glamorous style of Italian-American women from mafia family sagas. Think fur coats, animal prints, gold jewelry, dramatic makeup, and an unapologetic maximalist approach to luxury. It's about looking expensive, powerful, and slightly dangerous.",
    characteristics: [
      "Fur coats and animal prints",
      "Bold gold jewelry",
      "Dramatic, glamorous makeup",
      "Maximalist approach to luxury",
      "Italian designer influences",
      "Dark, rich colors with bold accents",
      "Power and confidence",
    ],
    colorPalette: [
      { name: "Black", hex: "#000000" },
      { name: "Leopard", hex: "#C19A6B" },
      { name: "Blood Red", hex: "#660000" },
      { name: "Gold", hex: "#FFD700" },
      { name: "Deep Burgundy", hex: "#4A0E0E" },
      { name: "White", hex: "#FFFFFF" },
    ],
    keyPieces: [
      "Faux fur coat",
      "Leopard print anything",
      "Gold chain necklaces",
      "Red lipstick",
      "Designer sunglasses",
      "Pointed-toe heels",
      "Velour tracksuit",
      "Statement earrings",
    ],
    celebrities: ["Jennifer Lopez", "Carmela Soprano", "Victoria Gotti", "The Real Housewives", "Cher"],
    doStyles: [
      "Go bold with animal prints",
      "Layer gold jewelry generously",
      "Embrace fur (faux preferred)",
      "Choose dramatic makeup",
      "Mix textures luxuriously",
      "Wear your confidence",
    ],
    dontStyles: [
      "Don't be subtle or understated",
      "Avoid looking cheap or tacky",
      "Skip minimal jewelry",
      "Don't hold back on glamour",
      "Avoid boring basics alone",
      "Don't forget the attitude",
    ],
    relatedAesthetics: ["dark-feminine", "indie-sleaze"],
    seasonConnection: "Bold enough for Deep Autumn or Dark Winter palettes",
    outfitIdeas: [
      {
        name: "Sunday Dinner",
        description: "Family gathering glamour",
        pieces: ["Leopard print dress", "Gold hoops", "Black heels", "Red lip"],
      },
      {
        name: "Ladies Who Lunch",
        description: "Daytime drama",
        pieces: ["Faux fur jacket", "Black turtleneck", "Gold chains", "Designer bag"],
      },
      {
        name: "Night Out",
        description: "Full mob wife energy",
        pieces: ["Velvet dress", "Fur stole", "Statement earrings", "Strappy heels"],
      },
    ],
  },
  cottagecore: {
    id: "cottagecore",
    name: "Cottagecore",
    tagline: "Romanticizing simple life, one wildflower at a time.",
    description:
      "Cottagecore celebrates a romanticized vision of rural life. It embraces floral prints, handmade crafts, vintage-inspired clothing, and a connection to nature. Think prairie dresses, wicker baskets, bread baking, and pressing flowers. It's about slowing down and finding beauty in simple, pastoral living.",
    characteristics: [
      "Floral and gingham prints",
      "Soft, natural color palette",
      "Vintage and handmade elements",
      "Flowing, feminine silhouettes",
      "Nature-inspired accessories",
      "Pastoral and romantic vibes",
      "DIY and craft aesthetic",
    ],
    colorPalette: [
      { name: "Sage Green", hex: "#9DC183" },
      { name: "Butter Yellow", hex: "#FFFACD" },
      { name: "Dusty Rose", hex: "#DCAE96" },
      { name: "Cream", hex: "#FFFDD0" },
      { name: "Soft Brown", hex: "#A0522D" },
      { name: "Lavender", hex: "#E6E6FA" },
    ],
    keyPieces: [
      "Floral midi dress",
      "Puff-sleeve blouse",
      "Wicker basket bag",
      "Lace-up boots",
      "Straw hat",
      "Embroidered cardigan",
      "Apron",
      "Linen everything",
    ],
    celebrities: ["Florence Welch", "Taylor Swift (folklore era)", "Keira Knightley", "Saoirse Ronan"],
    doStyles: [
      "Embrace florals and gingham",
      "Choose natural, breathable fabrics",
      "Include vintage or handmade pieces",
      "Layer with cardigans and aprons",
      "Opt for soft, muted colors",
      "Add nature-inspired accessories",
    ],
    dontStyles: [
      "Avoid synthetic or modern materials",
      "Skip harsh, bright colors",
      "Don't go too structured or corporate",
      "Avoid overly trendy pieces",
      "Skip flashy accessories",
      "Don't forget the romantic details",
    ],
    relatedAesthetics: ["balletcore", "coastal-grandmother", "coquette"],
    seasonConnection: "Harmonizes with Soft Autumn or Soft Summer palettes",
    outfitIdeas: [
      {
        name: "Garden Day",
        description: "Tending to the flowers",
        pieces: ["Floral midi dress", "Linen apron", "Garden boots", "Straw hat"],
      },
      {
        name: "Farmers Market",
        description: "Morning shopping",
        pieces: ["Puff-sleeve blouse", "Midi skirt", "Wicker basket", "Leather sandals"],
      },
      {
        name: "Picnic",
        description: "Afternoon in the meadow",
        pieces: ["Gingham dress", "Cardigan", "Ankle boots", "Ribbon in hair"],
      },
    ],
  },
  minimalist: {
    id: "minimalist",
    name: "Minimalist",
    tagline: "Less is not just more — it's everything.",
    description:
      "The Minimalist aesthetic strips fashion down to its essential elements. It values simplicity, clean lines, neutral colors, and thoughtful curation over excess. Every piece in a minimalist wardrobe serves a purpose and can mix seamlessly with others. Quality and intention are paramount.",
    characteristics: [
      "Clean lines and simple silhouettes",
      "Neutral color palette",
      "Quality over quantity",
      "Functional and versatile pieces",
      "Absence of unnecessary details",
      "Intentional wardrobe curation",
      "Timeless over trendy",
    ],
    colorPalette: [
      { name: "White", hex: "#FFFFFF" },
      { name: "Black", hex: "#000000" },
      { name: "Grey", hex: "#808080" },
      { name: "Beige", hex: "#F5F5DC" },
      { name: "Navy", hex: "#000080" },
      { name: "Camel", hex: "#C19A6B" },
    ],
    keyPieces: [
      "White t-shirt",
      "Black trousers",
      "Cashmere sweater",
      "Tailored blazer",
      "Simple sneakers",
      "Leather tote",
      "Classic watch",
      "Slip dress",
    ],
    celebrities: ["Phoebe Philo", "Jil Sander", "Mary-Kate Olsen", "Tilda Swinton", "Angelina Jolie"],
    doStyles: [
      "Invest in quality basics",
      "Keep colors neutral and cohesive",
      "Focus on fit and tailoring",
      "Choose versatile pieces",
      "Avoid unnecessary embellishments",
      "Curate intentionally",
    ],
    dontStyles: [
      "Avoid busy patterns and prints",
      "Skip trendy, fast-fashion items",
      "Don't over-accessorize",
      "Avoid poor-quality fabrics",
      "Skip anything that doesn't serve a purpose",
      "Don't clutter your wardrobe",
    ],
    relatedAesthetics: ["clean-girl", "quiet-luxury", "old-money"],
    seasonConnection: "Works beautifully with any season in muted, tonal palettes",
    outfitIdeas: [
      {
        name: "Everyday Essential",
        description: "Effortless daily uniform",
        pieces: ["White tee", "Black trousers", "White sneakers", "Leather tote"],
      },
      {
        name: "Work Ready",
        description: "Professional simplicity",
        pieces: ["Tailored blazer", "Silk blouse", "Tailored pants", "Pointed flats"],
      },
      {
        name: "Evening Simple",
        description: "Understated elegance",
        pieces: ["Black slip dress", "Simple gold earrings", "Strappy heels", "Clutch"],
      },
    ],
  },
  streetwear: {
    id: "streetwear",
    name: "Streetwear",
    tagline: "Where culture meets concrete.",
    description:
      "Streetwear is a fashion movement born from skateboarding, hip-hop, and youth culture. It's characterized by comfortable, casual clothing with an urban edge — think sneakers, hoodies, graphic tees, and statement pieces from hyped brands. It's about self-expression, cultural relevance, and being part of a community.",
    characteristics: [
      "Sneakers as statement pieces",
      "Graphic tees and hoodies",
      "Oversized silhouettes",
      "Brand and logo prominence",
      "Limited edition and hype pieces",
      "Cultural and music influences",
      "Comfort meets cool",
    ],
    colorPalette: [
      { name: "Black", hex: "#000000" },
      { name: "White", hex: "#FFFFFF" },
      { name: "Earth Tones", hex: "#8B4513" },
      { name: "Neon Accents", hex: "#39FF14" },
      { name: "Grey", hex: "#808080" },
      { name: "Primary Colors", hex: "#FF0000" },
    ],
    keyPieces: [
      "Limited edition sneakers",
      "Graphic hoodie",
      "Oversized tee",
      "Cargo pants",
      "Dad cap",
      "Crossbody bag",
      "Track jacket",
      "Chunky sneakers",
    ],
    celebrities: ["Travis Scott", "A$AP Rocky", "Billie Eilish", "Kanye West", "Virgil Abloh"],
    doStyles: [
      "Invest in quality sneakers",
      "Mix high and low brands",
      "Play with proportions",
      "Include statement pieces",
      "Express personal style",
      "Stay culturally aware",
    ],
    dontStyles: [
      "Avoid looking costume-like",
      "Don't fake limited editions",
      "Skip trying too hard",
      "Avoid mismatched aesthetics",
      "Don't ignore comfort",
      "Skip cultural appropriation",
    ],
    relatedAesthetics: ["indie-sleaze", "dark-feminine"],
    seasonConnection: "Adapts to any season with appropriate layering",
    outfitIdeas: [
      {
        name: "Hype Day",
        description: "Flexing the collection",
        pieces: ["Graphic hoodie", "Cargo pants", "Limited sneakers", "Crossbody bag"],
      },
      {
        name: "Casual Cool",
        description: "Everyday streetwear",
        pieces: ["Oversized tee", "Joggers", "Classic sneakers", "Dad cap"],
      },
      {
        name: "Night Out",
        description: "Elevated street style",
        pieces: ["Track jacket", "Black jeans", "Statement sneakers", "Chain accessories"],
      },
    ],
  },
};

// Export aesthetic type IDs for generateStaticParams
export const aestheticTypeIds = Object.keys(aestheticTypes);

// Get aesthetic type by ID (handles slug format)
export function getAestheticType(id: string): AestheticType | undefined {
  return aestheticTypes[id];
}

// FAQ data for each aesthetic type
export interface AestheticFAQ {
  question: string;
  answer: string;
}

const aestheticFAQs: Record<string, AestheticFAQ[]> = {
  "old-money": [
    {
      question: "What is the Old Money aesthetic?",
      answer: "The Old Money aesthetic is a fashion and lifestyle style that embodies understated luxury, timeless elegance, and generational wealth. It emphasizes quality over quantity, classic silhouettes, natural fabrics like cashmere and silk, and avoids flashy logos or trendy pieces. Think Ivy League prep, European aristocracy, and country club elegance.",
    },
    {
      question: "How do I dress Old Money on a budget?",
      answer: "You can achieve the Old Money look affordably by: 1) Thrifting for quality basics in neutral colors, 2) Investing in a few key pieces like a good blazer or cashmere sweater, 3) Focusing on fit and tailoring (even inexpensive clothes look expensive when tailored), 4) Avoiding logos and choosing timeless silhouettes, and 5) Maintaining your clothes well so they look pristine.",
    },
    {
      question: "What colors are Old Money?",
      answer: "The Old Money color palette consists of muted, sophisticated neutrals: navy blue, cream, camel/tan, burgundy, forest green, charcoal gray, and ivory. These colors are timeless, elegant, and easy to mix and match. Avoid bright, neon, or trendy colors that scream 'trying too hard.'",
    },
    {
      question: "What brands are considered Old Money?",
      answer: "Classic Old Money brands include Ralph Lauren, Brooks Brothers, J.Crew, Loro Piana, Brunello Cucinelli, Hermès (especially scarves), Barbour, and L.L. Bean. However, true Old Money style is about the look, not the label — wearing logo-free, quality pieces matters more than the brand name.",
    },
  ],
  "clean-girl": [
    {
      question: "What is the Clean Girl aesthetic?",
      answer: "The Clean Girl aesthetic is a minimalist beauty and fashion trend that emphasizes natural, healthy-looking skin, slicked-back hair, minimal makeup, and simple neutral-toned clothing. It's about looking effortlessly polished and put-together without appearing to try too hard. Key elements include dewy skin, gold hoop earrings, and quality basics.",
    },
    {
      question: "How do I achieve Clean Girl makeup?",
      answer: "Clean Girl makeup focuses on skincare-first, natural beauty: 1) Start with well-hydrated, glowing skin, 2) Use minimal base — just concealer where needed or light tinted moisturizer, 3) Groom and fill brows naturally, 4) Add a touch of cream blush, 5) Use clear or nude lip gloss, 6) Skip heavy eye makeup — maybe just mascara and subtle highlight.",
    },
    {
      question: "What hair is Clean Girl?",
      answer: "The signature Clean Girl hairstyle is the slicked-back bun or low ponytail. Use gel or hair oil to smooth flyaways, pull hair back tightly, and secure in a neat bun or ponytail. Other Clean Girl styles include natural waves, straight and glossy hair, or a sleek middle part. The key is looking groomed and polished.",
    },
    {
      question: "Is Clean Girl aesthetic still trending?",
      answer: "Yes, the Clean Girl aesthetic remains popular in 2025, though it has evolved. It represents a shift toward minimalism, self-care, and natural beauty that resonates with many people. While trends come and go, the core principles — healthy skin, minimal makeup, and effortless style — are timeless.",
    },
  ],
  "dark-feminine": [
    {
      question: "What is the Dark Feminine aesthetic?",
      answer: "The Dark Feminine aesthetic embraces the mysterious, powerful, and seductive side of femininity. It draws from archetypes like the femme fatale, enchantress, and queen. This style features dark colors (black, burgundy, emerald), luxurious textures (velvet, silk, leather), bold silhouettes, and dramatic makeup. It's about commanding presence and unapologetic power.",
    },
    {
      question: "What is Dark Feminine energy?",
      answer: "Dark Feminine energy refers to embracing traditionally suppressed aspects of femininity: power, seduction, mystery, intuition, and self-assurance. It's about owning your desires, setting boundaries, and not dimming yourself to make others comfortable. The aesthetic and fashion choices reflect this energy through bold, intentional styling.",
    },
    {
      question: "How do I dress Dark Feminine?",
      answer: "To dress Dark Feminine: 1) Build a base of black and deep jewel tones, 2) Incorporate luxurious textures like velvet, silk, lace, and leather, 3) Choose bold silhouettes — fitted, structured, or dramatically draped, 4) Accessorize with statement jewelry featuring dark stones, 5) Opt for pointed-toe heels and structured bags, 6) Keep makeup dramatic with dark lips or smoky eyes.",
    },
    {
      question: "What's the difference between Dark Feminine and Gothic?",
      answer: "While both aesthetics use dark colors, they differ in vibe and references. Gothic draws from Victorian, punk, and horror influences with an emphasis on darkness, rebellion, and subcultural identity. Dark Feminine is more mainstream-luxury, focusing on power, sensuality, and sophistication. Dark Feminine is boardroom-to-date-night; Gothic is more alternative and subcultural.",
    },
  ],
  coquette: [
    {
      question: "What is the Coquette aesthetic?",
      answer: "The Coquette aesthetic is a romantic, feminine style characterized by soft pink colors, bows, ribbons, lace, and delicate fabrics. It draws from French girl style and ballet-inspired fashion. The look is flirtatious yet innocent, playful yet sophisticated — think Lana Del Rey meets ballerina meets French ingenue.",
    },
    {
      question: "How do I dress Coquette?",
      answer: "To achieve the Coquette look: 1) Embrace soft colors — blush pink, cream, baby blue, and lavender, 2) Add bows and ribbons to hair, clothes, or accessories, 3) Choose delicate fabrics like lace, satin, and silk, 4) Opt for feminine silhouettes — A-line skirts, ruffle tops, fit-and-flare dresses, 5) Accessorize with pearls and dainty jewelry, 6) Wear ballet flats or Mary Janes.",
    },
    {
      question: "What's the difference between Coquette and Soft Girl?",
      answer: "While both aesthetics are feminine and use soft colors, Coquette has a more mature, romantic, and slightly seductive edge. Soft Girl is youthful and cute with influences from K-pop and Instagram culture. Coquette draws from French romance and ballet; Soft Girl draws from social media and modern cuteness. Coquette uses more lace and satin; Soft Girl uses more fluffy textures and pastels.",
    },
    {
      question: "Is Coquette still popular in 2025?",
      answer: "Yes, Coquette remains a major aesthetic trend in 2025. The romantic, feminine style has resonated deeply with those seeking softer, more traditionally feminine fashion. It has evolved to include more sophisticated interpretations and merged with other trends like Quiet Luxury for a more grown-up take on the bow-adorned look.",
    },
  ],
  "quiet-luxury": [
    {
      question: "What is Quiet Luxury?",
      answer: "Quiet Luxury, also called stealth wealth, is an aesthetic that values exceptional quality, impeccable fit, and timeless design over flashy branding. It's the antithesis of logo-heavy fashion. Think unbranded cashmere, perfectly tailored trousers, and accessories only connoisseurs recognize. It whispers wealth rather than shouting it.",
    },
    {
      question: "What brands are Quiet Luxury?",
      answer: "Quiet Luxury brands include Loro Piana, Brunello Cucinelli, The Row, Khaite, Totême, and Jil Sander. These brands are known for exceptional quality and minimal branding. However, true Quiet Luxury isn't about specific brands — it's about the approach: quality fabrics, perfect fit, timeless design, and no visible logos.",
    },
    {
      question: "How do I achieve Quiet Luxury on a budget?",
      answer: "Achieve the Quiet Luxury look affordably by: 1) Buying fewer, better pieces, 2) Focusing on natural fabrics (wool, cashmere, silk, cotton), 3) Getting clothes tailored for perfect fit, 4) Sticking to a neutral color palette, 5) Maintaining clothes meticulously, 6) Avoiding logos and branding, 7) Shopping consignment for luxury basics.",
    },
    {
      question: "What's the difference between Old Money and Quiet Luxury?",
      answer: "While overlapping, Old Money is a broader lifestyle aesthetic evoking generational wealth, prep school traditions, and country club culture. Quiet Luxury is specifically about fashion choices — unbranded luxury, quality materials, and understated elegance. Old Money might include preppy patterns and heritage brands; Quiet Luxury is purely about stealth wealth through quality.",
    },
  ],
  "indie-sleaze": [
    {
      question: "What is the Indie Sleaze aesthetic?",
      answer: "Indie Sleaze is a fashion aesthetic that channels the chaotic, party-girl energy of the late 2000s and early 2010s. Think Brooklyn warehouse parties, band tees, skinny jeans, leather jackets, smudged eyeliner, and an I-just-rolled-out-of-bed-looking-cool vibe. It's deliberately disheveled, unapologetically fun, and anti-polished.",
    },
    {
      question: "When was Indie Sleaze popular?",
      answer: "Indie Sleaze originally dominated fashion from roughly 2006-2012, associated with celebrities like Kate Moss, the Olsen twins (early era), Alexa Chung, and musicians from the indie rock scene. It saw a major revival starting in 2022 and continues to influence fashion in 2025, especially among those nostalgic for pre-Instagram party culture.",
    },
    {
      question: "How do I dress Indie Sleaze?",
      answer: "To nail Indie Sleaze: 1) Start with skinny jeans or a mini skirt, 2) Add a vintage band tee or graphic top, 3) Layer with a leather jacket, 4) Wear combat boots or ankle boots, 5) Accessorize with studded belts and layered necklaces, 6) Keep makeup smudged and lived-in (smoky eyes, nude lips), 7) Style hair messy and textured — bedhead is the goal.",
    },
    {
      question: "What music goes with Indie Sleaze?",
      answer: "Indie Sleaze is deeply connected to indie rock, electro, and dance music from the late 2000s: The Strokes, Yeah Yeah Yeahs, MGMT, LCD Soundsystem, Justice, Bloc Party, and Arctic Monkeys. The aesthetic was born in the same NYC and London scenes that produced this music, and they remain intrinsically linked.",
    },
  ],
  "coastal-grandmother": [
    {
      question: "What is the Coastal Grandmother aesthetic?",
      answer: "Coastal Grandmother is a lifestyle aesthetic inspired by Nancy Meyers movies — think Diane Keaton in 'Something's Gotta Give.' It evokes the effortless elegance of a woman who summers in the Hamptons or Maine: linen everything, cozy knits, comfortable elegance, farmers markets, beach walks, and reading on the porch. It's relaxed sophistication with a coastal, literary vibe.",
    },
    {
      question: "How do I dress Coastal Grandmother?",
      answer: "To achieve the Coastal Grandmother look: 1) Choose natural, breathable fabrics — especially linen and cotton, 2) Stick to soft, neutral colors: white, cream, sand, soft blue, sage, 3) Opt for comfortable, elegant pieces — wide-leg pants, button-downs, cable-knit sweaters, 4) Accessorize with straw bags, simple gold jewelry, and comfortable loafers or sandals, 5) Prioritize comfort without sacrificing style.",
    },
    {
      question: "What age is Coastal Grandmother for?",
      answer: "Despite the name, Coastal Grandmother isn't about age — it's about a lifestyle and aesthetic sensibility. People of any age can embrace the linen-and-farmers-market lifestyle. The trend became popular among Gen Z and Millennials precisely because it celebrates comfort, quality, and a slower pace of life regardless of your actual age.",
    },
    {
      question: "What brands are Coastal Grandmother?",
      answer: "Coastal Grandmother brands include Eileen Fisher, Jenni Kayne, L.L. Bean, Madewell (for basics), J.Crew, TOAST, and Everlane. Look for brands emphasizing natural fabrics, neutral colors, and comfortable elegance. Local linen shops, artisan markets, and European brands like A.P.C. and Margaret Howell also fit the aesthetic.",
    },
  ],
  balletcore: [
    {
      question: "What is the Balletcore aesthetic?",
      answer: "Balletcore brings the elegance of the dance studio into everyday fashion. It's characterized by wrap tops, leg warmers, ballet flats, soft pink tones, and delicate fabrics like tulle and satin. This aesthetic celebrates the discipline, grace, and romanticism of ballet while making it wearable for non-dancers.",
    },
    {
      question: "How do I dress Balletcore?",
      answer: "To achieve Balletcore style: 1) Incorporate wrap and tie details — wrap cardigans, tie-front tops, 2) Choose soft colors: ballet pink, cream, blush, soft grey, 3) Add ballet-inspired pieces: leg warmers, off-shoulder tops, tulle skirts, 4) Wear ballet flats or satin pointed flats, 5) Include ribbon accents in hair or on clothing, 6) Keep silhouettes graceful and feminine.",
    },
    {
      question: "What's the difference between Balletcore and Coquette?",
      answer: "While both are romantic and feminine, Balletcore specifically references ballet and dance studios — wrap tops, leg warmers, leotard-inspired pieces. Coquette is broader romantic femininity with French influence — bows, lace, pearl jewelry. Balletcore is more athletic-adjacent and structured; Coquette is more decorative and frilly. They can overlap but have distinct inspirations.",
    },
    {
      question: "Can you wear Balletcore without being a dancer?",
      answer: "Absolutely! Balletcore is a fashion aesthetic, not a dress code for dancers. Anyone can incorporate ballet-inspired elements into their wardrobe. The key is capturing the grace and elegance of ballet through fashion choices — you don't need to know a plié from a pirouette to rock a wrap top and ballet flats.",
    },
  ],
  "mob-wife": [
    {
      question: "What is the Mob Wife aesthetic?",
      answer: "The Mob Wife aesthetic channels the bold, glamorous style of Italian-American women from mafia family sagas — think Carmela Soprano or Victoria Gotti. It features fur coats, animal prints (especially leopard), gold jewelry, dramatic makeup, and an unapologetic maximalist approach to luxury. It's about looking expensive, powerful, and slightly dangerous.",
    },
    {
      question: "How do I dress Mob Wife?",
      answer: "To nail the Mob Wife look: 1) Invest in a faux fur coat — the bigger, the better, 2) Embrace animal prints, especially leopard, 3) Layer gold jewelry generously — chains, hoops, rings, 4) Choose dramatic makeup — red or dark lips, defined brows, 5) Add designer (or designer-looking) sunglasses, 6) Wear pointed-toe heels and structured bags, 7) Include velour and velvet pieces.",
    },
    {
      question: "Is Mob Wife aesthetic problematic?",
      answer: "The Mob Wife aesthetic is a fashion trend inspired by media portrayals, not an endorsement of organized crime. Like any aesthetic drawing from cultural imagery, it's important to appreciate the style without romanticizing harmful realities. Most interpret it as celebrating bold, unapologetic femininity and Italian-American glamour rather than actual mob associations.",
    },
    {
      question: "What's the difference between Mob Wife and Dark Feminine?",
      answer: "While both are bold and powerful, Mob Wife is maximalist, cultural, and campy — think fur, leopard print, gold chains, and Italian-American flair. Dark Feminine is more refined and mysterious — think velvet, lace, dark jewel tones, and femme fatale energy. Mob Wife says 'look at me'; Dark Feminine says 'figure me out.'",
    },
  ],
  cottagecore: [
    {
      question: "What is the Cottagecore aesthetic?",
      answer: "Cottagecore celebrates a romanticized vision of rural life: floral prints, handmade crafts, vintage-inspired clothing, and deep connection to nature. Think prairie dresses, wicker baskets, bread baking, and pressing flowers. It's about slowing down, finding beauty in simple pastoral living, and embracing traditional crafts and domesticity.",
    },
    {
      question: "How do I dress Cottagecore?",
      answer: "To achieve Cottagecore style: 1) Embrace florals, gingham, and natural patterns, 2) Choose natural fabrics — linen, cotton, muslin, 3) Opt for vintage-inspired silhouettes — prairie dresses, puff sleeves, A-line skirts, 4) Add handmade or vintage pieces, 5) Accessorize with straw hats, wicker baskets, and delicate jewelry, 6) Wear comfortable boots or sandals suitable for nature.",
    },
    {
      question: "Is Cottagecore just for women?",
      answer: "No! Cottagecore is for everyone. For men and non-binary folks, Cottagecore can include linen shirts, earth-toned knitwear, suspenders, vintage-style trousers, and nature-inspired accessories. The core values — connection to nature, craftsmanship, slower living — transcend gender. It's about the lifestyle and values, not just feminine dresses.",
    },
    {
      question: "What activities go with Cottagecore?",
      answer: "Cottagecore activities include: gardening and flower pressing, baking bread and homemade pastries, foraging for mushrooms or berries, hand-crafts like embroidery and knitting, reading in nature, tea parties, journaling, picnics, farmers market trips, and learning traditional skills. It's as much a lifestyle as a fashion aesthetic.",
    },
  ],
  minimalist: [
    {
      question: "What is the Minimalist aesthetic in fashion?",
      answer: "Minimalist fashion strips style down to its essential elements: clean lines, neutral colors, quality basics, and thoughtful curation. Every piece serves a purpose and mixes seamlessly with others. It's about quality over quantity, timeless over trendy, and intentional wardrobe building. Think capsule wardrobes and the '30-piece closet' concept.",
    },
    {
      question: "How do I build a Minimalist wardrobe?",
      answer: "To build a Minimalist wardrobe: 1) Declutter and keep only what you truly wear, 2) Invest in quality basics in neutral colors (white, black, grey, navy, camel), 3) Focus on fit — tailored pieces look more expensive, 4) Choose versatile items that work together, 5) Avoid trends and patterns, 6) Maintain a capsule wardrobe of 30-40 carefully chosen pieces.",
    },
    {
      question: "What colors are Minimalist?",
      answer: "Minimalist color palettes typically include: black, white, grey, navy, beige, camel, cream, and soft earth tones. The key is cohesion — all your pieces should work together. Some minimalists embrace all-black; others prefer cream and beige tones. The point is intentional simplicity, not a rigid color rule.",
    },
    {
      question: "Is Minimalism boring?",
      answer: "Minimalism is only boring if executed without intention. True Minimalist style is about quality, perfect fit, and subtle details that discerning eyes notice. A perfectly tailored black blazer, an exquisite cashmere sweater, or the drape of quality silk isn't boring — it's sophisticated. Minimalism challenges you to make simplicity interesting through quality and proportion.",
    },
  ],
  streetwear: [
    {
      question: "What is the Streetwear aesthetic?",
      answer: "Streetwear is a fashion movement born from skateboarding, hip-hop, and youth culture. It's characterized by comfortable, casual clothing with an urban edge: sneakers, hoodies, graphic tees, and statement pieces from hyped brands. It's about self-expression, cultural relevance, community belonging, and the intersection of fashion, music, and street culture.",
    },
    {
      question: "How do I start wearing Streetwear?",
      answer: "To start with Streetwear: 1) Invest in quality sneakers — they're the foundation, 2) Get comfortable basics: well-fitting hoodies, graphic tees, joggers, 3) Learn to play with proportions — oversized tops with fitted bottoms or vice versa, 4) Follow streetwear culture — brands, drops, and community, 5) Start simple before chasing hype pieces, 6) Express your own style rather than copying looks exactly.",
    },
    {
      question: "What are the biggest Streetwear brands?",
      answer: "Major Streetwear brands include: Supreme, Off-White, A Bathing Ape (BAPE), Palace, Stüssy, Fear of God, Kith, and Comme des Garçons (CDG). Nike and Adidas collaborations are also central to streetwear culture. However, streetwear also celebrates smaller, independent brands and vintage finds over just wearing expensive labels.",
    },
    {
      question: "Is Streetwear only for young people?",
      answer: "No! While streetwear emerged from youth culture, people of all ages wear it today. The key is finding your own interpretation — you don't need to dress like a teenager. Mature streetwear might lean toward cleaner silhouettes, quality materials, and subtle branding while still embracing the comfort, sneakers, and cultural elements that define the aesthetic.",
    },
  ],
};

// Get FAQs for an aesthetic type (with fallback to generic FAQs)
export function getAestheticFAQs(type: string): AestheticFAQ[] {
  const typeData = aestheticTypes[type];
  const typeFAQs = aestheticFAQs[type];

  if (typeFAQs) {
    return typeFAQs;
  }

  // Generic fallback FAQs
  const name = typeData?.name || type;
  return [
    {
      question: `What is the ${name} aesthetic?`,
      answer: typeData?.description || `The ${name} aesthetic is a distinct fashion and lifestyle style with its own unique characteristics, color palette, and cultural influences.`,
    },
    {
      question: `How do I dress ${name}?`,
      answer: `To dress ${name}, focus on the key pieces and color palette associated with this aesthetic. Start with basics and gradually incorporate more specific elements as you develop your personal take on the style.`,
    },
    {
      question: `What celebrities have the ${name} aesthetic?`,
      answer: typeData ? `Celebrities known for the ${name} aesthetic include ${typeData.celebrities.slice(0, 3).join(", ")}, among others.` : `Various celebrities embody the ${name} aesthetic through their fashion choices and public appearances.`,
    },
    {
      question: `Can I mix ${name} with other aesthetics?`,
      answer: `Yes! Many people blend elements from different aesthetics to create their own unique style. ${name} can be combined with related aesthetics for a personalized look that expresses your individuality.`,
    },
  ];
}
